import React, { useEffect, useState, useCallback, useRef, forwardRef, useImperativeHandle } from 'react'
import { AnimatePresence } from 'framer-motion'
import { Bell, Loader } from 'lucide-react'

import { notificationConfig, shouldRespectReducedMotion } from '../../constants/notificationConfig'

import type { NotificationButtonProps, NotificationButtonState, GiggleTriggerReason } from './NotificationButton.types'
import * as S from './NotificationButton.styles'

export const NotificationButton = forwardRef<
    { triggerGiggle?: () => void; triggerBadgeBounce?: () => void; triggerRipple?: () => void },
    NotificationButtonProps
>(function NotificationButton(
    { notifications, userType, isOpen, isLoading = false, unreadCount, onToggle, onGiggleTrigger },
    ref,
) {
    const [state, setState] = useState<NotificationButtonState>({
        unreadCount: 0,
        shouldGiggle: false,
        shouldBounce: false,
        hasNewNotification: false,
        lastNotificationId: null,
    })

    const lastGiggleTime = useRef<number>(0)
    const giggleTimeout = useRef<NodeJS.Timeout | null>(null)
    const bounceTimeout = useRef<NodeJS.Timeout | null>(null)
    const previousUnreadCount = useRef<number>(0)
    const [externalRipple, setExternalRipple] = useState(false)
    const externalRippleTimeout = useRef<NodeJS.Timeout | null>(null)

    const triggerGiggle = useCallback(
        (_reason: GiggleTriggerReason['reason'], _notificationId?: string) => {
            const now = Date.now()

            // Respect cooldown period
            if (now - lastGiggleTime.current < notificationConfig.bell.giggleCooldown) {
                return
            }

            // Respect reduced motion preference
            if (shouldRespectReducedMotion()) {
                return
            }

            setState((prev) => ({ ...prev, shouldGiggle: true }))

            lastGiggleTime.current = now

            // Call external handler if provided
            onGiggleTrigger?.()

            // Stop giggle after animation completes
            if (giggleTimeout.current) {
                clearTimeout(giggleTimeout.current)
            }

            giggleTimeout.current = setTimeout(() => {
                setState((prev) => ({ ...prev, shouldGiggle: false }))
            }, notificationConfig.bell.giggleDuration)
        },
        [onGiggleTrigger],
    )

    // Use provided unreadCount and detect new notifications
    useEffect(() => {
        const activeNotifications = notifications.filter((n) => !n.is_dismissed)
        const latestNotification =
            activeNotifications.length > 0
                ? activeNotifications.sort(
                      (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
                  )[0]
                : null

        const hasNewNotification = unreadCount > previousUnreadCount.current
        const newNotificationId = hasNewNotification && latestNotification ? latestNotification.id : null

        setState((prevState) => ({
            ...prevState,
            unreadCount: unreadCount,
            hasNewNotification,
            lastNotificationId: newNotificationId || prevState.lastNotificationId,
            // no periodic pulse; rely on ripple/giggle/bounce
        }))

        // Trigger giggle for new notifications
        if (hasNewNotification && latestNotification) {
            triggerGiggle('newNotification', latestNotification.id)
        }

        previousUnreadCount.current = unreadCount
    }, [notifications, userType, unreadCount, triggerGiggle])

    // Removed periodic pulse; simpler, quieter UX

    // Cleanup timeouts on unmount
    useEffect(() => {
        return () => {
            if (giggleTimeout.current) {
                clearTimeout(giggleTimeout.current)
            }
            if (bounceTimeout.current) {
                clearTimeout(bounceTimeout.current)
            }
            // no periodic pulse timers to clear
            if (externalRippleTimeout.current) {
                clearTimeout(externalRippleTimeout.current)
            }
        }
    }, [])

    // Handle hover events
    const handleMouseEnter = useCallback(() => {
        if (state.unreadCount > 0) {
            triggerGiggle('hover')
        }
    }, [state.unreadCount, triggerGiggle])

    // Handle manual giggle trigger (from control panel)
    const handleManualGiggle = useCallback(() => {
        triggerGiggle('manual')
    }, [triggerGiggle])

    // Handle manual badge bounce trigger (from control panel)
    const handleManualBadgeBounce = useCallback(() => {
        if (state.unreadCount > 0) {
            setState((prev) => ({ ...prev, shouldBounce: true }))

            // Clear existing bounce timeout
            if (bounceTimeout.current) {
                clearTimeout(bounceTimeout.current)
            }

            // Reset bounce after animation
            bounceTimeout.current = setTimeout(() => {
                setState((prev) => ({ ...prev, shouldBounce: false }))
            }, 400) // Match animation duration
        }
    }, [state.unreadCount])

    // Expose manual trigger methods through ref
    useImperativeHandle(
        ref,
        () => ({
            triggerGiggle: handleManualGiggle,
            triggerBadgeBounce: handleManualBadgeBounce,
            triggerRipple: () => {
                if (shouldRespectReducedMotion()) return
                setExternalRipple(true)
                if (externalRippleTimeout.current) clearTimeout(externalRippleTimeout.current)
                externalRippleTimeout.current = setTimeout(() => setExternalRipple(false), 1200)
            },
        }),
        [handleManualGiggle, handleManualBadgeBounce],
    )

    const handleClick = useCallback(() => {
        onToggle()
    }, [onToggle])

    // Determine bell animation state
    // Slightly slower and lower amplitude jiggle for comfort
    const bellAnimation = state.shouldGiggle ? { rotate: [0, -12, 12, -8, 8, -4, 4, 0] } : { rotate: 0 }

    // Determine badge visibility and animation
    const badgeVisible = state.unreadCount > 0
    const badgeCount = Math.min(state.unreadCount, notificationConfig.badge.maxDisplayCount + 1)
    const displayCount =
        badgeCount > notificationConfig.badge.maxDisplayCount
            ? `${notificationConfig.badge.maxDisplayCount}+`
            : badgeCount.toString()

    const shouldRipple = (state.hasNewNotification || externalRipple) && !isOpen && !shouldRespectReducedMotion()
    return (
        <S.NotificationButtonContainer>
            <S.BellButton
                onClick={handleClick}
                onMouseEnter={handleMouseEnter}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                $isOpen={isOpen}
                $shouldGiggle={state.shouldGiggle}
                $isLoading={isLoading}
                aria-label={`Notifications ${badgeVisible ? `(${state.unreadCount} unread)` : ''}`}
                aria-expanded={isOpen}
                aria-haspopup="true"
                disabled={isLoading}
            >
                <AnimatePresence>
                    {shouldRipple && (
                        <S.BellRipple
                            key={`ripple-${state.lastNotificationId || 'na'}`}
                            initial={{ opacity: 0.2, scale: 0.8 }}
                            animate={{ opacity: 0, scale: 1.6 }}
                            exit={{ opacity: 0 }}
                            transition={{ duration: 1.2, ease: 'easeOut' }}
                            aria-hidden
                        />
                    )}
                </AnimatePresence>
                <S.BellIcon
                    animate={bellAnimation}
                    transition={{
                        duration: notificationConfig.bell.giggleDuration / 1000,
                        ease: 'easeInOut',
                        times: [0, 0.15, 0.3, 0.45, 0.6, 0.78, 0.9, 1],
                    }}
                    $isLoading={isLoading}
                >
                    {isLoading ? <Loader size={20} /> : <Bell size={20} />}
                </S.BellIcon>

                <AnimatePresence mode="wait">
                    {badgeVisible && (
                        <S.NotificationBadge
                            key={`badge-${state.unreadCount}`}
                            initial={{ scale: 0, opacity: 0 }}
                            animate={
                                state.shouldBounce
                                    ? { scale: [1, 1.3, 1], y: [0, -4, 0], opacity: 1 }
                                    : { scale: 1, y: 0, opacity: 1 }
                            }
                            exit={{ scale: 0, opacity: 0 }}
                            transition={{ duration: 0.4, ease: 'easeOut' }}
                            $count={badgeCount}
                            $shouldBounce={state.hasNewNotification || state.shouldBounce}
                            $isVisible={badgeVisible}
                            aria-hidden="true"
                        >
                            {displayCount}
                        </S.NotificationBadge>
                    )}
                </AnimatePresence>

                <S.ScreenReaderText>
                    {isLoading
                        ? 'Loading notifications...'
                        : badgeVisible
                          ? `${state.unreadCount} unread notification${state.unreadCount === 1 ? '' : 's'}`
                          : 'No unread notifications'}
                </S.ScreenReaderText>
            </S.BellButton>
        </S.NotificationButtonContainer>
    )
})

// Export component with display name for debugging
NotificationButton.displayName = 'NotificationButton'
